import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2, Home, Briefcase, X, Star } from 'lucide-react';
import LocationAutocomplete from './LocationAutocomplete';
import * as api from '../services/api';

export default function SavedLocations({ onClose }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    location_name: '',
    address: '',
    lat: null,
    lng: null,
    is_primary: false
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const response = await api.getUserLocations();
      if (response.success) {
        setLocations(response.locations || []);
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
      alert('Failed to load saved locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async () => {
    if (!formData.location_name || !formData.address || !formData.lat || !formData.lng) {
      alert('Please fill in all fields and select a valid address');
      return;
    }

    try {
      const response = await api.addUserLocation(formData);
      if (response.success) {
        await loadLocations();
        resetForm();
        setShowAddForm(false);
      } else {
        alert('Failed to add location: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Add location error:', error);
      alert('Failed to add location');
    }
  };

  const handleUpdateLocation = async () => {
    if (!editingId) return;

    try {
      const response = await api.updateUserLocation(editingId, formData);
      if (response.success) {
        await loadLocations();
        resetForm();
        setEditingId(null);
      } else {
        alert('Failed to update location');
      }
    } catch (error) {
      console.error('Update location error:', error);
      alert('Failed to update location');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Delete this location?')) return;

    try {
      const response = await api.deleteUserLocation(id);
      if (response.success) {
        await loadLocations();
      } else {
        alert('Failed to delete location');
      }
    } catch (error) {
      console.error('Delete location error:', error);
      alert('Failed to delete location');
    }
  };

  const startEdit = (location) => {
    setEditingId(location.id);
    setFormData({
      location_name: location.location_name,
      address: location.address,
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lng),
      is_primary: Boolean(location.is_primary)
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      location_name: '',
      address: '',
      lat: null,
      lng: null,
      is_primary: false
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const getLocationIcon = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('home')) return Home;
    if (lowerName.includes('work') || lowerName.includes('office')) return Briefcase;
    return MapPin;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">My Saved Locations</h3>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Location
          </button>
        )}
      </div>

      <p className="text-sm text-gray-600">
        Save your frequent locations to get automatic ride recommendations within 3.5km
      </p>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="p-4 bg-gray-50 rounded-lg border-2 border-emerald-200 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">{editingId ? 'Edit Location' : 'Add New Location'}</h4>
            <button onClick={resetForm} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location Name</label>
            <input
              type="text"
              placeholder="e.g. Home, Work, Gym"
              value={formData.location_name}
              onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <LocationAutocomplete
              value={formData.address}
              onChange={(value) => setFormData({ ...formData, address: value })}
              onPlaceSelect={(location) => setFormData({
                ...formData,
                address: location.address,
                lat: location.lat,
                lng: location.lng
              })}
              placeholder="Select address from map"
              icon="pickup"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_primary}
              onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
              className="w-4 h-4"
            />
            <Star className={`w-4 h-4 ${formData.is_primary ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
            <span className="text-sm">Set as primary location</span>
          </label>

          <div className="flex gap-2">
            <button
              onClick={editingId ? handleUpdateLocation : handleAddLocation}
              className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
              {editingId ? 'Update' : 'Add'} Location
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Locations List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading locations...</div>
      ) : locations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MapPin className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No saved locations yet</p>
          <p className="text-sm">Add locations to get ride recommendations</p>
        </div>
      ) : (
        <div className="space-y-2">
          {locations.map(location => {
            const Icon = getLocationIcon(location.location_name);
            return (
              <div
                key={location.id}
                className="p-4 border rounded-lg hover:bg-gray-50 flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <Icon className={`w-5 h-5 mt-0.5 ${
                    location.is_primary ? 'text-yellow-500' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{location.location_name}</h4>
                      {location.is_primary && (
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{location.address}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Added {new Date(location.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(location)}
                    className="p-2 hover:bg-gray-100 rounded">
                    <Edit className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteLocation(location.id)}
                    className="p-2 hover:bg-red-50 rounded">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}